import * as http from "api/http";
import { SERVICES_URL, handleError } from "api/helper";
import { ProgramService, TemplateService } from "@adfenix/types";

const post = http.post;
const get = http.get;
const put = http.put;
const del = http.del;

const authBaseUrl = "template/auth/v1";
const authCampaignContentUrl = "template/auth/v1/campaign-content";
const authImageTemplateUrl = "template/auth/v2/image-template";
const authEmailTemplate = "template/auth/v2/email";

export const createAdTemplate = ({
  template,
  channelType
}: {
  template: TemplateService.CreateFacebookContentTemplateRequest;
  channelType: ProgramService.ChannelType;
}): Promise<string> =>
  post(
    `${SERVICES_URL}/${authCampaignContentUrl}/${channelType.toLowerCase()}`,
    template
  ).catch(handleError);

export const getAdTemplate = async (
  templateId?: string,
  channel?: TemplateService.ChannelType
): Promise<TemplateService.FacebookContentTemplateModel> => {
  if (!channel || !templateId) {
    return Promise.reject();
  }
  return get(
    `${SERVICES_URL}/${authCampaignContentUrl}/${channel}/${templateId}`
  ).catch(handleError);
};

export const updateAdTemplate = async ({
  template,
  channel
}: {
  template: TemplateService.FacebookContentTemplateModel;
  channel?: TemplateService.ChannelType;
}): Promise<void> => {
  if (!channel) {
    return Promise.reject();
  }

  return put(
    `${SERVICES_URL}/${authCampaignContentUrl}/${channel}`,
    template
  ).catch(handleError);
};

export const getTemplatesForOwner = async (
  subjectType: TemplateService.SubjectType,
  subjectId?: string
): Promise<TemplateService.ContentTemplateModels> => {
  if (!subjectId) {
    return Promise.reject();
  }
  return get(
    `${SERVICES_URL}/${authCampaignContentUrl}/${subjectType}/${subjectId}`
  ).catch(handleError);
};

export const deleteTemplate = async (
  channel: TemplateService.ChannelType | undefined,
  templateId: string | undefined
): Promise<void> => {
  if (!channel || !templateId) {
    return Promise.reject();
  }
  return del(
    `${SERVICES_URL}/${authCampaignContentUrl}/${channel.toLowerCase()}/${templateId}`
  ).catch(handleError);
};

export const createImageTemplate = (
  createImageTemplateRequest: Required<TemplateService.CreateImageTemplateRequest>
): Promise<TemplateService.ImageTemplateModel> => {
  return post(
    `${SERVICES_URL}/${authImageTemplateUrl}`,
    createImageTemplateRequest
  );
};

export const updateImageTemplate = (
  updateImageTemplateRequest: TemplateService.ImageTemplateModel
): Promise<TemplateService.ImageTemplateModel> => {
  return put(
    `${SERVICES_URL}/${authImageTemplateUrl}`,
    updateImageTemplateRequest
  );
};

export const getImageTemplate = (
  id?: string
): Promise<TemplateService.ImageTemplateModel> => {
  if (!id) {
    return Promise.reject();
  }
  return get(`${SERVICES_URL}/${authImageTemplateUrl}/${id}`);
};

export const deleteImageTemplate = (templateId: string) => {
  return del(`${SERVICES_URL}/${authImageTemplateUrl}/${templateId}`);
};

export const getAvailableBookmarks = (
  subjectType?: TemplateService.SubjectAttributeType
): Promise<TemplateService.BookmarkModel[]> => {
  return get(
    `${SERVICES_URL}/${authBaseUrl}/bookmark/available/${subjectType}`
  );
};

export const previewImageTemplate = (
  model: TemplateService.PreviewImageTemplateRequest
): Promise<string> => {
  return post(
    `${SERVICES_URL}/${authImageTemplateUrl}/preview-for-subject-type`,
    model
  );
};

export const fillImageTemplate = (
  request: TemplateService.FillImageTemplateRequest
): Promise<TemplateService.FilledImageTemplateModel> => {
  return post(`${SERVICES_URL}/${authImageTemplateUrl}/fill`, request);
};

export const fillTemplate = (
  channel: TemplateService.ChannelType,
  request: TemplateService.FillContentTemplateRequest
): Promise<
  | TemplateService.FillFacebookContentTemplateResponse
  | TemplateService.FillInstagramContentTemplateResponse
  | TemplateService.FillTwitterContentTemplateResponse
> => {
  return post(
    `${SERVICES_URL}/${authCampaignContentUrl}/${channel}/fill`,
    request
  );
};

export const getAvailableEmailTemplates = (
  ownerId: string
): Promise<TemplateService.EmailTemplateOverviewModel[]> => {
  return get(`${SERVICES_URL}/${authEmailTemplate}/available/${ownerId}`);
};

export const fillBatchImageTemplates = (
  request: TemplateService.BatchFillImageTemplateRequest
): Promise<TemplateService.FilledImageTemplateModel[]> =>
  post(`${SERVICES_URL}/${authImageTemplateUrl}/fill-batch`, request).catch(
    handleError
  );

export const getSlideShow = (
  payload: TemplateService.UploadedSlideShowRequest
) =>
  post(`${SERVICES_URL}/template/auth/v1/slide-show/create`, payload).catch(
    handleError
  );
